/**
 * 数量与包装单位换算 —— 前后端唯一实现（Master Spec §16、决策 D-05、架构书 §6）。
 *
 * 铁律：
 *   1. 库存一律以 **基本单位的整数（bigint）** 存储与运算，永不使用浮点。
 *   2. 换算因子 factorToBase 为 **正整数**，多级包装存绝对因子（1 托 = 960 支）。
 *   3. 允许小数输入（如 0.5 箱），**当且仅当**换算到基本单位后为精确整数；否则拒绝。
 *   4. 显示优先精确分解（`1 箱 + 16 个`）；约值仅作辅助且必须标注，永不参与计算。
 */
export class QuantityError extends Error {
    code;
    params;
    constructor(code, params = {}) {
        super(code);
        this.code = code;
        this.params = params;
        this.name = 'QuantityError';
    }
}
const DECIMAL_RE = /^([+-]?)(\d+)(?:\.(\d+))?$/;
export function parseDecimal(input) {
    const raw = typeof input === 'number' ? numberToPlainString(input) : input.trim();
    const m = DECIMAL_RE.exec(raw);
    if (!m)
        throw new QuantityError('QUANTITY.NOT_A_NUMBER', { input: raw });
    const [, sign, intPart = '0', fracPart = ''] = m;
    const digits = `${intPart}${fracPart}`;
    const mantissa = BigInt(digits) * (sign === '-' ? -1n : 1n);
    return { mantissa, scale: fracPart.length };
}
/** 避免 JS 对大数/小数使用科学计数法（1e-7 → "1e-7"），改为普通十进制串 */
function numberToPlainString(n) {
    if (!Number.isFinite(n))
        throw new QuantityError('QUANTITY.NOT_A_NUMBER', { input: String(n) });
    if (!/e/i.test(String(n)))
        return String(n);
    return n.toFixed(20).replace(/0+$/, '').replace(/\.$/, '');
}
function pow10(n) {
    let r = 1n;
    for (let i = 0; i < n; i++)
        r *= 10n;
    return r;
}
// ---------------------------------------------------------------------------
// 输入 → 基本单位
// ---------------------------------------------------------------------------
/**
 * 把「数量 + 单位」换算为基本单位整数。
 *
 * @throws QuantityError QUANTITY.NOT_DIVISIBLE 当结果不是精确整数时（D-05）
 *
 * @example toBaseQuantity('0.5', 24n)  // → 12n
 * @example toBaseQuantity('0.3', 24n)  // → throws NOT_DIVISIBLE（7.2 非整数）
 * @example toBaseQuantity('10', 24n)   // → 240n
 */
export function toBaseQuantity(input, factorToBase) {
    if (factorToBase <= 0n) {
        throw new QuantityError('QUANTITY.INVALID_FACTOR', { factor: factorToBase.toString() });
    }
    const { mantissa, scale } = parseDecimal(input);
    const divisor = pow10(scale);
    const numerator = mantissa * factorToBase;
    if (numerator % divisor !== 0n) {
        throw new QuantityError('QUANTITY.NOT_DIVISIBLE', {
            input: String(input),
            factor: factorToBase.toString(),
        });
    }
    return numerator / divisor;
}
/** 输入是否能被该单位精确表示（供输入框实时校验，不抛异常） */
export function isExactlyRepresentable(input, factorToBase) {
    try {
        toBaseQuantity(input, factorToBase);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * 当输入无法用当前单位精确表示时，给出相邻的两个合法数量。
 *
 * 设计说明：不要"建议换一个单位"——用户输入 0.3 箱时，0.3 托恰好是精确的（288 支），
 * 但把「0.3 托」作为建议毫无意义。用户真正需要知道的是：7.2 支不存在，请选 7 支或 8 支。
 *
 * @example suggestExactAlternatives('0.3', 24n)
 *          // → { inexactBase: '7.2', lowerBase: 7n, upperBase: 8n }
 */
export function suggestExactAlternatives(input, factorToBase) {
    if (factorToBase <= 0n) {
        throw new QuantityError('QUANTITY.INVALID_FACTOR', { factor: factorToBase.toString() });
    }
    const { mantissa, scale } = parseDecimal(input);
    const divisor = pow10(scale);
    const numerator = mantissa * factorToBase;
    // bigint 除法向零取整；对负数需要修正为数学意义上的 floor
    const truncated = numerator / divisor;
    const remainder = numerator % divisor;
    const lowerBase = remainder < 0n ? truncated - 1n : truncated;
    const upperBase = remainder === 0n ? lowerBase : lowerBase + 1n;
    return { inexactBase: formatFraction(numerator, divisor), lowerBase, upperBase };
}
/** 把 numerator/divisor 渲染为十进制字符串，仅用于错误提示文案 */
function formatFraction(numerator, divisor) {
    if (divisor === 1n)
        return numerator.toString();
    const negative = numerator < 0n;
    const abs = negative ? -numerator : numerator;
    const whole = abs / divisor;
    const frac = (abs % divisor).toString().padStart(divisor.toString().length - 1, '0');
    const trimmed = frac.replace(/0+$/, '');
    const body = trimmed.length > 0 ? `${whole}.${trimmed}` : whole.toString();
    return negative ? `-${body}` : body;
}
/**
 * 把基本单位数量贪心分解为多级包装表示：240 支 → 10 箱；200 支 → 8 箱 + 8 支。
 * 负数按绝对值分解并置 negative 标志（负库存必须能正常显示）。
 */
export function decompose(baseQty, units) {
    const base = units.find((u) => u.isBase);
    if (!base)
        throw new QuantityError('QUANTITY.NO_BASE_UNIT');
    const negative = baseQty < 0n;
    let remaining = negative ? -baseQty : baseQty;
    const descending = [...units]
        .filter((u) => u.factorToBase > 0n)
        .sort((a, b) => b.factorToBase > a.factorToBase ? 1 : b.factorToBase < a.factorToBase ? -1 : 0);
    const parts = [];
    for (const unit of descending) {
        if (remaining === 0n)
            break;
        const count = remaining / unit.factorToBase;
        if (count > 0n) {
            parts.push({ unit, count });
            remaining -= count * unit.factorToBase;
        }
    }
    // 数量为 0 时显示「0 <基本单位>」而不是空字符串
    if (parts.length === 0)
        parts.push({ unit: base, count: 0n });
    return { negative, parts, totalBase: baseQty };
}
/**
 * 精确表示字符串：`8 case + 8 piece`。
 * 单位名由调用方通过 labelOf 注入（i18n：后端不返回人类语言，A-11）。
 */
export function formatExact(baseQty, units, labelOf, separator = ' + ') {
    const d = decompose(baseQty, units);
    const body = d.parts.map((p) => `${p.count} ${labelOf(p.unit, p.count)}`).join(separator);
    return d.negative ? `-${body}` : body;
}
/**
 * 约值，**仅供辅助显示**（D-05）。调用方必须在 UI 上加「≈」并标明为约值。
 * 该值永不参与计算、永不写库、永不出现在导出的数值列中。
 */
export function approximateIn(baseQty, unit, fractionDigits = 2) {
    if (unit.factorToBase <= 0n) {
        throw new QuantityError('QUANTITY.INVALID_FACTOR', { factor: unit.factorToBase.toString() });
    }
    const scale = pow10(fractionDigits);
    const scaled = (baseQty * scale) / unit.factorToBase;
    return Number(scaled) / Number(scale);
}
/** available = on_hand − reserved。批次级概念，位置级不存在此概念（Spec 附录 A-3） */
export function availableBase(q) {
    return q.qtyOnHandBase - q.reservedBase;
}
/**
 * I2c 守卫（D-15）：非预留出库不得使批次在手跌破已预留量。
 * @returns 允许出库的最大数量（可能为 0；若批次已处于侵占状态则为 0 而非负数）
 */
export function maxOrdinaryOutboundBase(q) {
    const avail = availableBase(q);
    return avail > 0n ? avail : 0n;
}
//# sourceMappingURL=quantity.js.map